import { sqItemFinderStore } from '@/core/core.stores';
import { updateSearch } from '@/itemFinder/itemFinder.actions';
import { SearchTypeEnum } from '@/itemFinder/itemFinder.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqDatasourcesApi } from '@/sdk/api/DatasourcesApi';
import { sqHierarchiesApi } from '@/sdk/api/HierarchiesApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqStoredTablesApi } from '@/sdk/api/StoredTablesApi';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { OperatorEnum } from '@/sdk/model/PropertyFilterInputV1';
import { doTrack } from '@/track/track.service';
import { base64guid } from '@/utilities/guid.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type {
  AIPayload,
  HierarchyLevelColumn,
  HierarchySearch,
  HierarchySearchOptions,
  Search,
  SearchType,
  StoredTableSearch,
} from '@/itemFinder/itemFinder.constants';
import type { DatasourceLabel, HierarchyPath } from '@/itemFinder/itemFinder.store';
import type { FixedListSearchV1 } from '@/sdk/model/FixedListSearchV1';
import type { HierarchySearchOutputV1 } from '@/sdk/model/HierarchySearchOutputV1';
import type { HierarchySearchV1 } from '@/sdk/model/HierarchySearchV1';
import type { ItemFinderInputV1 } from '@/sdk/model/ItemFinderInputV1';
import type { ItemFinderOutputV1 } from '@/sdk/model/ItemFinderOutputV1';
import type { PropertyFilterInputV1 } from '@/sdk/model/PropertyFilterInputV1';
import type { PropertyInputV1 } from '@/sdk/model/PropertyInputV1';
import type { PropertySearchV1 } from '@/sdk/model/PropertySearchV1';
import type { StoredTableSearchV1 } from '@/sdk/model/StoredTableSearchV1';
import type * as models from '@/sdk/model/models';
import type { Datasource, SelectedSearchItem } from '@/search/search.types';
import type { TrackInformation } from '@/track/track.types';
import type { SelectOptionIF } from '@/usage/Usage.page';

export const createUpdateItemFinder = async (
  itemFinder: ItemFinderInputV1,
  itemFinderId?: string,
  showInVantage?: boolean,
): Promise<ItemFinderOutputV1> => {
  const { data: itemFinderOutput } = await (itemFinderId
    ? sqItemsApi.updateItemFinder(itemFinder, { id: itemFinderId })
    : sqItemsApi.createItemFinder(itemFinder));

  if (!showInVantage) {
    const trackInformation: TrackInformation = {
      scopedTo: itemFinder.scopedTo,
      searchType: itemFinder.propertySearches ? 'Property' : 'Manual',
      propertySearches: JSON.stringify(itemFinder.propertySearches),
      fixedListSearches: JSON.stringify(itemFinder.fixedListSearches),
    };
    doTrack('Scaling Table Configuration', 'Create Item Finder', trackInformation);
  }

  if (!showInVantage && itemFinder.cronSchedule) {
    doTrack('Scaling Table Configuration', 'Set Schedule', {
      numberOfSchedules: itemFinder?.cronSchedule.length,
      schedules: itemFinder?.cronSchedule,
    });
  }

  return itemFinderOutput;
};

export const removeDatasourceProperties = (searchId: string) => {
  const search = sqItemFinderStore.searches.find((search) => search.searchId === searchId);

  if (search) {
    const datasourceIdProperty = search.predicates?.find(
      (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceId,
    );
    const datasourceClassProperty = search.predicates?.find(
      (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceClass,
    );

    if (datasourceClassProperty) {
      updateSearch({
        searchId,
        action: 'UPDATE_PROPERTY',
        propertyId: datasourceClassProperty.propertyId,
        propertyData: {
          datasources: [],
        },
      });
    }

    if (datasourceIdProperty) {
      updateSearch({
        searchId,
        propertyId: datasourceIdProperty.propertyId,
        action: 'REMOVE_PROPERTY',
      });
    }
  }
};

export const loadStoredTableColumns = async (storedTableId: string): Promise<ReactSelectOption[]> => {
  try {
    const { data } = await sqStoredTablesApi.getStoredTable({ id: storedTableId });

    return data.columns.map((col) => ({
      text: col.columnName,
      value: col.id,
    }));
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return [];
  }
};

export const loadHierarchySearchOptions = async (hierarchyId: string): Promise<HierarchySearchOptions> => {
  try {
    const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
    const { data: tableDef } = await sqTableDefinitionsApi.getTableDefinition({ id: hierarchy.tableDefinition.id });
    const extraColumns: { text: string; value: string }[] =
      !!hierarchy.aliasColumnId && tableDef.columnDefinitions.some((col) => col.id === hierarchy.aliasColumnId)
        ? [{ text: SeeqNames.Properties.Alias, value: SeeqNames.Properties.Alias }]
        : [];

    const columns = tableDef.columnDefinitions
      .filter(
        (col) =>
          col.columnName !== SeeqNames.MaterializedTables.ItemIdColumn &&
          col.columnName !== SeeqNames.MaterializedTables.DatumIdColumn &&
          col.columnType !== ColumnTypeEnum.UUID,
      )
      .map((col) => ({
        text: col.columnName,
        value: col.columnName,
      }));

    const levelViewColumns = hierarchy.viewColumns
      .filter((col) => col.groupOrder !== undefined)
      .sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0));

    const levelColumnOptions = levelViewColumns.map((viewCol, index) => {
      const colDef = tableDef.columnDefinitions.find((col) => col.id === viewCol.columnDefinition);
      return {
        columnId: viewCol.columnDefinition,
        columnName: colDef?.columnName ?? '',
        columnType: colDef?.columnType ?? '',
        orderValue: index + 1,
      };
    });

    return {
      hierarchyTableProperties: extraColumns.concat(columns),
      hierarchyLevelColumns: levelColumnOptions,
      hierarchy: { id: hierarchy.id, name: hierarchy.name, type: hierarchy.type },
      itemTable: {
        id: tableDef.id,
        name: tableDef.name,
        type: tableDef.type,
        columnId: tableDef.columnDefinitions.find((col) => col.columnName === SeeqNames.MaterializedTables.ItemIdColumn)
          ?.id,
      },
    };
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    throw error;
  }
};

/**
 * Converts a list of HierarchyPaths into SelectedSearchItems with proper ancestor chains and IDs.
 *
 * @param hierarchyPaths - The list of hierarchy paths to convert.
 * @param hierarchyLevelColumns - The level column definitions for the hierarchy.
 * @param searchItemTable - The item table selected for this search, used to determine UUID columns.
 * @returns An array of SelectedSearchItems representing the selected nodes.
 */
export const getHierarchySelectedNodes = (
  hierarchyPaths: HierarchyPath[],
  hierarchyLevelColumns: HierarchyLevelColumn[],
  searchItemTable: SelectedSearchItem | undefined,
): SelectedSearchItem[] => {
  function buildUniqueHierarchyNodeId(path: HierarchyPath): string {
    return path.nodes.reduce((generatedId, node) => {
      const pathColumn = hierarchyLevelColumns.find((col) => col.columnId === node.columnId)!;

      if ((node && pathColumn.columnType === ColumnTypeEnum.UUID) || node.columnId === searchItemTable?.columnId) {
        return node.value!;
      } else if (node && pathColumn.columnType !== ColumnTypeEnum.UUID) {
        return `${generatedId}|${node.value}-${pathColumn?.columnId}`;
      }
      return generatedId;
    }, '');
  }

  return hierarchyPaths.map((path) => {
    const finalNode = path.nodes[path.nodes.length - 1]!;
    const finalPathColumn = hierarchyLevelColumns.find((col) => col.columnId === finalNode.columnId);

    return {
      id: finalPathColumn?.columnType === ColumnTypeEnum.UUID ? finalNode.value! : buildUniqueHierarchyNodeId(path),
      columnId: finalNode.columnId,
      columnValue: finalNode.value,
      ancestors: path.nodes.slice(0, -1).map((node, nodeIndex) => {
        const pathColumn = hierarchyLevelColumns.find((col) => col.columnId === node.columnId);

        return {
          id:
            pathColumn?.columnType === ColumnTypeEnum.UUID
              ? node.value!
              : buildUniqueHierarchyNodeId({ nodes: path.nodes.slice(0, nodeIndex + 1) }),
          name: '',
          type: '',
          columnId: node.columnId,
          columnValue: node.value,
        };
      }),
    };
  });
};

export const addDatasourcePropertyAndValue = (
  searchId: string,
  propertyId: string,
  valueId: string,
  selectedDatasources: SelectOptionIF<Datasource>[],
  ancestorPredicateId?: string,
) => {
  const datasourceLabel: DatasourceLabel[] = [];
  const selectedDatasourceIds = selectedDatasources.map((datasource) => datasource.value.datasourceId as string);
  const datasourceClassValues: { value: string; propertyId: string; searchId: string; valueId: string }[] = [];
  const datasourceIdValues: { value: string; propertyId: string; searchId: string; valueId: string }[] = [];

  const search = sqItemFinderStore.searches.find((search) => search.searchId === searchId);
  const datasourceIdProperty = search?.predicates?.find(
    (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceId,
  );

  selectedDatasources.forEach((datasource) => {
    const { datasourceClass, datasourceId, name, id } = datasource.value;

    datasourceClassValues.push({
      value: datasourceClass as string,
      propertyId,
      searchId,
      valueId: base64guid(),
    });
    if (datasourceIdProperty) {
      datasourceIdValues.push({
        value: datasourceId as string,
        propertyId: datasourceIdProperty?.propertyId,
        searchId,
        valueId: base64guid(),
      });
    }
    datasourceLabel.push({
      label: datasource.value.name,
      value: {
        datasourceClass: datasourceClass as string,
        datasourceId: datasourceId as string,
        name,
        id: id as string,
      },
    });
  });

  // Update the property to contain the datasource id and name for the first selected datasource
  updateSearch({
    searchId,
    action: 'UPDATE_PROPERTY',
    propertyId,
    propertyData: {
      datasources: !selectedDatasources.length ? [] : datasourceLabel,
      values: !selectedDatasources.length ? [{ value: '', propertyId, searchId, valueId }] : datasourceClassValues,
      columnId: undefined,
      isColumnSelect: false,
    },
    ancestorPredicateId,
  });

  // If there is no datasourceId property, add it. Otherwise, update the values
  if (!datasourceIdProperty) {
    updateSearch({
      searchId,
      action: 'ADD_PROPERTY',
      propertyData: {
        propertyName: SeeqNames.Properties.DatasourceId,
        operator: OperatorEnum.STRINGCONTAINS,
        isColumnSelect: false,
      },
      propertyValues: selectedDatasourceIds,
      ancestorPredicateId,
    });
  } else {
    updateSearch({
      searchId,
      action: 'UPDATE_PROPERTY',
      propertyId: datasourceIdProperty.propertyId,
      propertyData: {
        values: !selectedDatasources.length
          ? [
              {
                value: '',
                propertyId: datasourceIdProperty.propertyId,
                searchId,
                valueId: datasourceIdProperty?.values[0]?.valueId,
              },
            ]
          : datasourceIdValues,
        columnId: undefined,
        isColumnSelect: false,
      },
      ancestorPredicateId,
    });
  }
};

export async function findDatasourceNamesAndIds(datasourceClass: string) {
  try {
    const { data } = await sqDatasourcesApi.getDatasources({ datasourceClass });
    if (data.datasources) {
      return {
        name: data.datasources[0].name,
        id: data.datasources[0].id,
        datasourceId: data.datasources[0].datasourceId,
      };
    }
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export const getItemFinder = async (itemFinderId: string): Promise<ItemFinderOutputV1 | undefined> => {
  try {
    const { data: itemFinderOutput } = await sqItemsApi.getItemFinder({ id: itemFinderId });
    return itemFinderOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const addEmptyPropertyToSearch = (search: Search) => {
  if (
    search.searchType === SearchTypeEnum.PROPERTY ||
    search.searchType === SearchTypeEnum.STOREDTABLE ||
    ((search.searchType as SearchType) === SearchTypeEnum.WORKBENCH && !search.predicates)
  ) {
    updateSearch({
      searchId: search.searchId,
      action: 'ADD_PROPERTY',
    });
  }
};

export const isValidAIPayload = (payload: Record<string, unknown>): payload is AIPayload => {
  const { propertySearches, fixedListSearches } = payload;
  if (!propertySearches && !fixedListSearches) {
    return false;
  }
  if (propertySearches && Array.isArray(propertySearches)) {
    if (!propertySearches.every(isPropertySearch)) {
      return false;
    }
  }
  if (fixedListSearches && Array.isArray(fixedListSearches)) {
    if (!fixedListSearches.every(isFixedListSearch)) {
      return false;
    }
  }
  return true;
};

export const isHierarchySearch = (search: unknown): search is HierarchySearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as HierarchySearchV1).searchType === SearchTypeEnum.HIERARCHY;
  return searchTypeCorrect && typeof search === 'object' && !!search && 'hierarchyId' in search;
};

export const isHierarchySearchOutput = (search: unknown): search is HierarchySearchOutputV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as HierarchySearchOutputV1).searchType === SearchTypeEnum.HIERARCHY;
  return (
    searchTypeCorrect &&
    typeof search === 'object' &&
    !!search &&
    'hierarchy' in search &&
    search.hierarchy !== undefined
  );
};

export const isStoredTableSearch = (search: unknown): search is StoredTableSearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as StoredTableSearchV1).searchType === SearchTypeEnum.STOREDTABLE;
  return searchTypeCorrect && typeof search === 'object' && !!search && 'storedTableId' in search;
};

const isFixedListSearch = (search: unknown): search is FixedListSearchV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as models.FixedListSearchV1).searchType === SearchTypeEnum.FIXEDLIST;
  const isIncludeCorrect = typeof (search as models.FixedListSearchV1).isInclude === 'boolean';
  const itemIdsCorrect = Array.isArray((search as models.FixedListSearchV1).itemIds);
  return searchTypeCorrect && isIncludeCorrect && itemIdsCorrect;
};

const isPropertySearch = (search: unknown): search is PropertySearchV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  if ((search as PropertySearchV1).searchType !== SearchTypeEnum.PROPERTY) {
    return false;
  }
  if (!('searchType' in search) || !('isInclude' in search) || !('types' in search)) {
    return false;
  }

  if ('predicates' in search) {
    if (!Array.isArray((search as PropertySearchV1).predicates)) {
      return false;
    }
    if (!(search as PropertySearchV1).predicates?.every(isPropertyFilterInputV1)) {
      return false;
    }
  }
  return true;
};

const isPropertyFilterInputV1 = (predicate: unknown): predicate is PropertyFilterInputV1 => {
  if (!predicate || typeof predicate !== 'object') {
    return false;
  }
  if (!('operator' in predicate) || !('propertyName' in predicate) || !('values' in predicate)) {
    return false;
  }
  if (!Array.isArray((predicate as PropertyFilterInputV1).values)) {
    return false;
  }
  return (predicate as PropertyFilterInputV1).values.every(isPropertyInputV1);
};

const isPropertyInputV1 = (value: unknown): value is PropertyInputV1 => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'value' in value;
};
